/* cc-units.js - 20 October 2026

   A cm / inches switch for the whole site. Charly, 20 Oct: "both, with a
   button on the whole website to switch sizes". This reverses the 30 Sep rule
   that inches live in one place only.

   HOW IT WORKS, and why it is a text layer rather than a change to every
   template. Sizes reach the page three ways: Liquid (cc-size-cm and
   cc-framed-cm), JavaScript (the builder writes its own option labels and
   notes), and static copy. One snippet could not cover all three. So the
   stored text stays centimetres everywhere, and this script rewrites any
   "N x M cm" or "N cm" it finds in VISIBLE TEXT when the visitor chooses
   inches, and puts the original back when they switch to cm. Nothing stored
   changes: SKUs, option values, alt text, meta and structured data stay in
   centimetres, because a search result or a screen reader should not change
   with a button.

   THE PRINT SIZES ARE EXACT. Our sizes ARE 8x12, 12x18, 16x24, 24x36, 32x48
   and 40x60 inches (Prodigi's own), so those come from a table rather than
   from dividing by 2.54. Framed sizes and every other length (wall widths,
   gaps) are conversions and are rounded to one decimal.

   WHAT IT LEAVES ALONE: scripts, styles, form fields, and anything marked
   data-cc-unit-skip. The PDP writes a dimensions line for the Blankwall AR app
   into #product-meta-dimensions-override and the app reads it as centimetres,
   so that element is skipped by id.

   COST. Visitors on centimetres pay nothing: no scan, no observer. Only when
   inches are chosen does it walk the page once and watch for new text (the
   builder rewrites its labels as you work). No scroll, wheel or touch listener
   of any kind, which this site has a recorded decision against.

   DEFAULT. Inches for the United States and Canada, centimetres everywhere
   else, as CC_Marketing recommended. A choice made with the switch is
   remembered in this browser and wins over the default. */
(function () {
  'use strict';

  var KEY = 'cc_unit';
  var PAIRS = {
    '20x30': [8, 12], '30x45': [12, 18], '40x60': [16, 24], '60x90': [24, 36], '80x120': [32, 48], '100x150': [40, 60],
    '24x34': [9.4, 13.4], '34x49': [13.4, 19.3], '44x64': [17.3, 25.2], '64x94': [25.2, 37], '84x124': [33.1, 48.8], '104x154': [40.9, 60.6]
  };
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, TEMPLATE: 1, TITLE: 1, INPUT: 1, SELECT: 0 };

  function readStored() { try { return localStorage.getItem(KEY); } catch (e) { return null; } }
  function store(v) { try { localStorage.setItem(KEY, v); } catch (e) {} }
  function defaultUnit() {
    var c = window.Shopify && window.Shopify.country;
    return c === 'US' || c === 'CA' ? 'in' : 'cm';
  }

  var unit = readStored() === 'in' || readStored() === 'cm' ? readStored() : defaultUnit();

  /* ----------------------------------------------------------- converting */
  function fmt(n) { return n % 1 === 0 ? String(n) : n.toFixed(1); }
  function inch(cm) {
    var v = cm / 2.54, r = Math.round(v);
    return Math.abs(v - r) < 0.06 ? String(r) : (Math.round(v * 10) / 10).toString();
  }
  /* A text node that is EXACTLY one of our known sizes with no unit written,
     "20 x 30" in a size tag, as Paper & Print and the gifts page show them.
     The unit is implied there, so without this the tags would stay in
     centimetres while everything around them turned to inches. Only exact
     matches count: a stray "2 x 3" in a sentence is left alone. */
  var BARE = /^\s*(\d{1,3})(\s*[\u00d7x]\s*)(\d{1,3})\s*$/;
  function bare(text) {
    var m = BARE.exec(text);
    if (!m) return null;
    var x = +m[1], y = +m[3], t = PAIRS[x + 'x' + y], flip = false;
    if (!t) { t = PAIRS[y + 'x' + x]; flip = true; }
    if (!t) return null;
    return (flip ? fmt(t[1]) + m[2] + fmt(t[0]) : fmt(t[0]) + m[2] + fmt(t[1])) + ' in';
  }
  /* THE WORD, AND "20 by 30". Added 21 Oct after a US visitor would have read
     "Sizes are in centimetres" under size buttons already showing inches, on
     every product page. Prose that names the unit in words is swapped with the
     numbers: "centimetres" becomes "inches", and a known size written "20 by 30"
     converts like "20 x 30". The singular "centimetre" is left alone because it
     only appears in idiom ("to the centimetre"), where swapping would be wrong. */
  var WORD = /\b(C|c)entimetres\b/g;
  var BYPAIR = /(\d{1,3})(\s+by\s+)(\d{1,3})(?!\d)/g;
  function words(text) {
    text = text.replace(WORD, function (m, c) { return c === 'C' ? 'Inches' : 'inches'; });
    return text.replace(BYPAIR, function (m, a, sep, b) {
      var x = +a, y = +b, t = PAIRS[x + 'x' + y];
      if (t) return fmt(t[0]) + sep + fmt(t[1]);
      t = PAIRS[y + 'x' + x];
      return t ? fmt(t[1]) + sep + fmt(t[0]) : m;
    });
  }
  function convert(text) {
    var b = bare(text);
    if (b) return text.replace(/\S[\s\S]*\S|\S/, b);
    if (text.indexOf('cm') === -1) return words(text);
    text = text.replace(/(\d{1,3})(\s*[×x]\s*)(\d{1,3})(\s*)cm\b/g, function (m, a, sep, b, sp) {
      var x = +a, y = +b, t = PAIRS[x + 'x' + y];
      if (t) return fmt(t[0]) + sep + fmt(t[1]) + sp + 'in';
      t = PAIRS[y + 'x' + x];
      if (t) return fmt(t[1]) + sep + fmt(t[0]) + sp + 'in';
      return inch(x) + sep + inch(y) + sp + 'in';
    });
    return words(text.replace(/(\d{1,4}(?:\.\d+)?)(\s*)cm\b/g, function (m, n, sp) { return inch(+n) + sp + 'in'; }));
  }

  /* ----------------------------------------------------------- the layer */
  /* node -> { orig, shown }. Only filled while inches are on. */
  var seen = new Map();
  var observer = null;

  function skipped(node) {
    for (var el = node.parentNode; el && el.nodeType === 1; el = el.parentNode) {
      if (SKIP_TAGS[el.tagName]) return true;
      if (el.id === 'product-meta-dimensions-override' || el.hasAttribute('data-cc-unit-skip')) return true;
    }
    return false;
  }

  function apply(node) {
    var rec = seen.get(node);
    /* our own write coming back through the observer: nothing to do */
    if (rec && node.data === rec.shown) return;
    var text = node.data;
    if ((text.indexOf('cm') === -1 && text.indexOf('entimetres') === -1 && text.indexOf(' by ') === -1 && !BARE.test(text)) || skipped(node)) { if (rec) seen.delete(node); return; }
    var out = convert(text);
    if (out === text) return;
    seen.set(node, { orig: text, shown: out });
    node.data = out;
  }

  function walk(root) {
    var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = w.nextNode())) apply(n);
  }

  /* Converted INSIDE the observer callback, not on a later frame. A mutation
     observer callback runs as a microtask before the browser paints, so the
     visitor never sees the centimetre text flash by, and the work still happens
     when the tab is in the background, where requestAnimationFrame is paused.
     The first version deferred to requestAnimationFrame and the product page's
     size labels stayed in centimetres after a size change. */
  function handle(node) {
    if (node.nodeType === 3) apply(node);
    else if (node.nodeType === 1 && node.isConnected) walk(node);
  }

  function watch() {
    if (observer || !window.MutationObserver) return;
    observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'characterData') handle(r.target);
        else if (r.addedNodes) for (var k = 0; k < r.addedNodes.length; k++) handle(r.addedNodes[k]);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }
  function unwatch() { if (observer) { observer.disconnect(); observer = null; } }

  function toInches() { walk(document.body); watch(); }
  function toCm() {
    unwatch();
    seen.forEach(function (rec, node) { if (node.isConnected && node.data === rec.shown) node.data = rec.orig; });
    seen.clear();
  }

  /* ----------------------------------------------------------------- UI */
  function paintSwitches() {
    document.querySelectorAll('[data-cc-unit-switch] [data-unit]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-unit') === unit ? 'true' : 'false');
    });
    document.documentElement.setAttribute('data-cc-unit', unit);
  }

  function set(next, remember) {
    if (next !== 'cm' && next !== 'in') return;
    var changed = next !== unit;
    unit = next;
    if (remember) store(next);
    if (changed) { if (unit === 'in') toInches(); else toCm(); }
    paintSwitches();
    if (changed) document.dispatchEvent(new CustomEvent('cc-units:change', { detail: { unit: unit } }));
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-cc-unit-switch] [data-unit]') : null;
    if (b) set(b.getAttribute('data-unit'), true);
  });

  window.ccUnits = { get: function () { return unit; }, set: function (u) { set(u, true); } };

  function start() {
    paintSwitches();
    if (unit === 'in') toInches();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
}());
